import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@govard/i18n-core/react';

import {
  getDashboardSignals,
  getDashboardSignalsQueryKey,
  getDashboardStatsQueryKey,
  getDashboardStats,
  getDashboardKpiSeries,
  getDashboardKpiSeriesQueryKey,
  getDashboardActivityQueryKey,
  getDashboardActivity,
  getDashboardOnboardingQueryKey,
  getDashboardOnboarding,
} from './api/dashboard.client.js';
import { listDeals } from '../deals/api/deals.client.js';
import { listHotLeads } from '../hot-leads/api/hot-leads.client.js';
import { listTasks } from '../tasks/api/tasks.client.js';
import { useCurrentScope } from '../../shared/scope/useCurrentScope.js';

import { buildDashboardViewModel, type DashboardViewModel } from './dashboard.vm.js';

export interface UseDashboardViewModelResult {
  viewModel: DashboardViewModel;
  dealsLoading: boolean;
  hotLeadsLoading: boolean;
  /** True when any widget query failed — widgets fall back to empty data. */
  hasError: boolean;
  /** Re-runs every widget query (manual retry for the error banner). */
  refetchAll: () => void;
}

export function useDashboardViewModel(): UseDashboardViewModelResult {
  const { t } = useTranslation();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId;

  const dealsQuery = useQuery({
    queryKey: ['dashboard', workspaceId ?? 'no-workspace', 'deals'],
    queryFn: () => listDeals(),
    retry: false,
    enabled: Boolean(workspaceId),
  });
  const hotLeadsQuery = useQuery({
    queryKey: ['dashboard', workspaceId ?? 'no-workspace', 'hot-leads'],
    queryFn: () => listHotLeads(),
    retry: false,
    enabled: Boolean(workspaceId),
  });
  const tasksQuery = useQuery({
    queryKey: ['dashboard', workspaceId ?? 'no-workspace', 'tasks'],
    queryFn: () => listTasks(),
    retry: false,
    enabled: Boolean(workspaceId),
  });
  const signalsQuery = useQuery({
    queryKey: workspaceId
      ? getDashboardSignalsQueryKey(workspaceId)
      : ['dashboard', 'no-workspace', 'signals'],
    queryFn: () => getDashboardSignals(),
    retry: false,
    enabled: Boolean(workspaceId),
  });

  const statsQuery = useQuery({
    queryKey: workspaceId
      ? getDashboardStatsQueryKey(workspaceId)
      : ['dashboard', 'no-workspace', 'stats'],
    queryFn: () => getDashboardStats(),
    retry: false,
    enabled: Boolean(workspaceId),
  });

  // KPI trend series (spec D parity). Failure is non-fatal: the strip simply
  // renders without deltas/sparklines, exactly like before the endpoint.
  const kpiSeriesQuery = useQuery({
    queryKey: workspaceId
      ? getDashboardKpiSeriesQueryKey(workspaceId)
      : ['dashboard', 'no-workspace', 'kpi-series'],
    queryFn: () => getDashboardKpiSeries(),
    retry: false,
    enabled: Boolean(workspaceId),
  });

  const activityQuery = useQuery({
    queryKey: workspaceId
      ? getDashboardActivityQueryKey(workspaceId)
      : ['dashboard', 'no-workspace', 'activity'],
    queryFn: () => getDashboardActivity(10),
    retry: false,
    enabled: Boolean(workspaceId),
  });

  const onboardingQuery = useQuery({
    queryKey: workspaceId
      ? getDashboardOnboardingQueryKey(workspaceId)
      : ['dashboard', 'no-workspace', 'onboarding'],
    queryFn: () => getDashboardOnboarding(),
    retry: false,
    enabled: Boolean(workspaceId),
  });

  // Queries fail with retry:false and every widget falls back to empty data —
  // without this aggregate an API outage is indistinguishable from a new,
  // genuinely empty workspace.
  const allQueries = [
    dealsQuery,
    hotLeadsQuery,
    tasksQuery,
    signalsQuery,
    statsQuery,
    kpiSeriesQuery,
    activityQuery,
    onboardingQuery,
  ];
  const hasError = allQueries.some((query) => query.isError);
  const refetchAll = (): void => {
    for (const query of allQueries) {
      void query.refetch();
    }
  };

  const viewModel = useMemo(
    () =>
      buildDashboardViewModel({
        deals: dealsQuery.data ?? [],
        hotLeads: hotLeadsQuery.data ?? [],
        tasks: tasksQuery.data ?? [],
        signals: signalsQuery.data ?? { scenarioRuns: [], pendingApprovals: [] },
        stats: statsQuery.data ?? {
          dealsTotal: 0,
          wonThisMonth: 0,
          hotLeadsCount: 0,
          avgDealValue: 0,
        },
        ...(kpiSeriesQuery.data !== undefined ? { kpiSeries: kpiSeriesQuery.data } : {}),
        activities: activityQuery.data ?? [],
        onboarding: onboardingQuery.data ?? {
          id: '',
          userId: '',
          workspaceId: workspaceId ?? '',
          completedSteps: [],
          dismissed: false,
          updatedAt: '',
        },
        t,
      }),
    [
      dealsQuery.data,
      hotLeadsQuery.data,
      tasksQuery.data,
      signalsQuery.data,
      statsQuery.data,
      kpiSeriesQuery.data,
      activityQuery.data,
      onboardingQuery.data,
      workspaceId,
      t,
    ],
  );

  return {
    viewModel,
    dealsLoading: dealsQuery.isLoading,
    hotLeadsLoading: hotLeadsQuery.isLoading,
    hasError,
    refetchAll,
  };
}
